<template lang="pug">
div
  template(v-if="isFetching")
    v-skeleton-loader(type="list-item-three-line")
    v-divider
    v-skeleton-loader(type="list-item-three-line@4")
  template(v-else)
    .main.py-3(:style="{ '--view-height': viewHeight }")
      div.d-flex.align-center.mx-8.my-2
        v-btn.ml-n3.mr-4(text icon @click="onClickButton")
          v-icon(color="neutral700" :size="24") mdi-arrow-left
        div.text-2xl.font-semibold.mr-4(v-if="isOTC") {{ $t('over_the_counter') }}
        div.text-2xl.font-semibold(:class="{ 'neutral500--text':isOTC }") {{ $t('invoice') }} {{ `#${invoice.rid}` }}
        v-spacer
        div.d-flex.align-center
          e-invoice-section.mr-2(:invoice="invoice" :transactions="transactions" inline)
          y-button.mr-2(label="Add item from past treatment" outlined size="sm" @click="onAddItemFromMedicalRecord")
          y-button(:label="$t('download_item', { item:$t('invoice').toLowerCase() })" prepend-icon="$yzi-download-02" icon-size="20" size="sm" :loading="isLoadingDownloadInvoice" @click="onDownloadInvoice")
      v-divider.mt-4.mb-6.mx-8
      v-row.mx-8(no-gutters)
        v-col(cols="1")
          .text-sm.font-medium.neutral500--text {{ $t('patient') }}
        v-col(cols="2")
          .d-flex
            .mr-2
              v-img.round-4xl(v-if="patient.profile_image" width="20" height="20" :src="patient.profile_image.file" cover)
                template(v-slot:placeholder)
                  v-row(no-gutters class="fill-height ma-0" align="center" justify="center")
                    v-progress-circular(indeterminate size="15" color="grey" width="2")
              v-card.neutral300.fill-height.round-4xl(v-else flat width="20" height="20")
                div.d-flex.align-center.justify-center.fill-height
                  v-icon(size="15" x-small) $yzi-user-03
            .text-sm.font-semibold.neutral700--text {{ patient.name }}
        v-col(cols="3")
          billing-info(:billingInfo="billingInfo" :patient="patient" @updated="fetchInvoice")
        v-col(v-if="hasRegistration" cols="3" offset="1")
          v-row(no-gutters)
            v-col(cols="3")
              .text-sm.font-medium.neutral500--text {{ $t('payment') }}
            v-col
              template(v-if="registration.default_payment.panel")
                .text-sm.neutral700--text.mb-1 {{ registration.default_payment.panel.name }}
                .text-xs.neutral600--text {{ registration.default_payment.note }}
              .text-sm.neutral700--text(v-else) {{ $t('self_pay') }}
        v-spacer
        v-col.text-right(cols="2")
          template(v-if="invoice.paid")
            .text-sm.neutral500--text.font-semibold.capitalize {{ $t('payment_status') }}
            .text-pricing.uppercase.font-semibold {{ $t('paid') }}
          template(v-else)
            .text-sm.neutral500--text.font-semibold {{ $t('balance_due') }}
            .text-pricing.font-semibold.no-wrap {{ totalBalance | displayPrice($currency) }}
      v-divider.mt-4.mt-lg-8
      treatment-table.white(:key="treatmentTableKey" :tableHeight="tableHeight" :leadPid="leadPid" :patient="patient" :parentTreatments="invoice.products" :parentDefaultPayment="registration.default_payment" :parentIncompletedDocuments="incompletedDocuments" :invoiceNo="invoice.rid" :isOTC="isOTC" :registrationStatus="registration.status" :parentActiveMedications="activeMedications" @changed="onTreatmentTableChanged" @addBulkTreatment="refetch")
      v-row.mr-4.mt-10(no-gutters)
        v-col(cols="3" offset="9")
          v-row(no-gutters)
            v-col.text-left.pl-4
              .text-sm.neutral700--text {{$t('subtotal') }}
            v-col.text-right
              .text-sm.neutral700--text.no-wrap {{ invoice.subtotal_amount | displayPrice }}
          template(v-if="allowedToAdjust")
            v-row.my-1(no-gutters)
              v-col
                v-divider.neutral300
            v-row(no-gutters)
              v-col.text-left.pl-4
                div(v-if="invoice.invoice_discount !== 0")
                  .d-flex.align-center
                    .text-sm.no-wrap Adjustment
                    v-btn(small icon @click="onDeleteAdjustment")
                      v-icon(small) mdi-close
                template(v-else)
                  payment-adjustment(:totalAmount="invoice.total_amount" :invoice="invoice" @updated="refetch" :positionTop="true" attach)
              v-col.text-right
                .text-sm.neutral700--text.no-wrap.pt-1 {{ invoice.invoice_discount > 0 ? '-' : '' }} {{ Math.abs(invoice.invoice_discount) | displayPrice }}
          v-row.my-1(no-gutters)
            v-col
              v-divider.neutral300
          v-row(no-gutters)
            v-col.text-left.pl-4
              payment-tax(:rates="taxRates" :taxRate="invoice.extra_data && invoice.extra_data.tax_rate" :taxAmount="invoice.tax_amount" hidePrice :invoice="invoice" @updated="fetchInvoice" menu-position="top")
            v-col.text-right
              .text-sm.neutral700--text.no-wrap.pt-1 {{ invoice.tax_amount | displayPrice }}
          v-row.my-1(no-gutters)
            v-col
              v-divider.neutral300
          v-row(no-gutters)
            v-col.text-left.pl-4
              .text-sm.neutral700--text {{$t('total') }}
            v-col.text-right
              .text-sm.neutral700--text.no-wrap {{ invoice.total_amount | displayPrice }}
          v-row.my-1(no-gutters)
            v-col
              v-divider.neutral300
          v-row(no-gutters)
            v-col.text-left.pl-4
              .text-sm.neutral700--text {{$t('paid') }}
            v-col.text-right
              .text-sm.neutral700--text.no-wrap {{ invoice.total_paid | displayPrice }}
          v-row.mt-1.mb-2(no-gutters)
            v-col
              v-divider.neutral300
          v-row(no-gutters)
            v-col.text-left.pl-4
              .text-lg.font-semibold {{$t('amount_due') }}
            v-col.text-right
              .text-lg.font-semibold.no-wrap {{ totalBalance | displayPrice }}
      div.py-12
      v-footer.white(app inset padless elevation="3" style="z-index: 12")
        .d-flex.flex-column.grow.mb-2
          v-divider
          .d-flex.justify-end.align-center.mt-2.mr-2
            v-btn.button-md(depressed color="primary" @click="onClickButton") {{ isFromOTC ? $t('proceed_to_payment') : $t('save_and_close') }}
  .dialogs
    medication-record-dialog(v-if="openMedicationRecordDialog" :open.sync="openMedicationRecordDialog" :parentPatient="patient" @updated="onMedicalRecordUpdated")
    incomplete-document-confirmation-dialog(v-if="showIncompleteDocumentConfirmationDialog" :open.sync="showIncompleteDocumentConfirmationDialog" @ignore="onIgnoreIncompleteDocument")
    download-invoice-receipt-dialog(v-if="openSelectInvoiceDialog" :open.sync="openSelectInvoiceDialog" :documents="invoiceTemplates" documentType="invoice")
    delete-payment-adjustment-dialog(v-if="openConfirmDeleteAdjustmentDialog" :open.sync="openConfirmDeleteAdjustmentDialog" @deleted="refetch")
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import TreatmentTable from '@/components/clinic/consultation/treatments/TreatmentTable.vue'
import MedicationRecordDialog from '@/components/clinic/dialogs/MedicationRecordDialog.vue'
import BillingInfo from '@/components/clinic/invoice/BillingInfo.vue'
import IncompleteDocumentConfirmationDialog from '@/components/clinic/dialogs/IncompleteDocumentConfirmationDialog.vue'
import DownloadInvoiceReceiptDialog from '@/components/clinic/dialogs/DownloadInvoiceReceiptDialog.vue'
import PaymentAdjustment from '@/components/clinic/checkout/PaymentAdjustment.vue'
import PaymentTax from '@/components/clinic/checkout/PaymentTax.vue'
import DeletePaymentAdjustmentDialog from '@/components/clinic/dialogs/DeletePaymentAdjustmentDialog.vue'
import EInvoiceSection from '@/components/clinic/checkout/EInvoiceSection.vue'

export default {
  name: 'Invoice',
  components: {
    TreatmentTable, MedicationRecordDialog, BillingInfo, IncompleteDocumentConfirmationDialog, DownloadInvoiceReceiptDialog, PaymentAdjustment, DeletePaymentAdjustmentDialog, PaymentTax, EInvoiceSection
  },
  props: ['pid', 'origin', 'data', 'register'],
  data () {
    return {
      isFetching: true,
      treatmentTableKey: 0,
      invoice: null,
      transactions: [],
      openMedicationRecordDialog: false,
      incompletedDocuments: [],
      showIncompleteDocumentConfirmationDialog: false,
      isLoadingDownloadInvoice: false,
      openSelectInvoiceDialog: false,
      invoiceTemplates: [],
      openConfirmDeleteAdjustmentDialog: false,
      taxRates: [],
      activeMedications: {
        isFetching: false,
        isError: false,
        data: []
      }
    }
  },
  async mounted () {
    try {
      this.isFetching = true
      let allFetching = [this.fetchInvoice(), this.fetchTransactions()]
      if (!this.isHq) {
        allFetching.push(this.fetchTaxRates())
      }
      await Promise.all([...allFetching])
      if (this.isHq) {
        await this.fetchTaxRates()
      }
      this.isFetching = false
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
    }
    this.setPopState()
  },
  beforeDestroy () {
    this.$store.commit('hq/resetSelectedCompany')
    window.removeEventListener('popstate', this.handlePopState)
  },
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      isDoctor: 'user/isDoctor',
      displaySize: 'ui/getDisplaySize',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    leadPid() {
      return this.pid
    },
    tableHeight() {
      return this.$vuetify.breakpoint.xl ? '45vh' : '30vh'
    },
    registration() {
      return this.invoice.registration || {}
    },
    patient() {
      return this.invoice.customer
    },
    isOTC() {
      // For migrated data, OTC is false
      if (!this.registration) return false

      return this.registration.code === 3
    },
    totalBalance() {
      if (!this.invoice) {
        return 0
      }
      return this.invoice.total_amount - this.invoice.total_paid
    },
    billingInfo() {
      return this.invoice.billing_address
    },
    isFromOTC () {
      if (this.register === 'otc') {
        return true
      }
      return false
    },
    hasRegistration () {
      return !!this.invoice.registration
    },
    fetchInvoiceParams () {
      return {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        categories: 'invoice'
      }
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true

      return false
    },
    allowedToAdjust () {
      if (this.company.genes.find(gene => gene.feature === 'lock_payment_adjustment') && !this.isAdminOrDoctor) return false

      return true
    },
    viewHeight () {
      switch (this.displaySize) {
        case 'xx-small':
          return '139vh'
        case 'x-small':
          return '124vh'
        case 'small':
          return '115vh'
        case 'medium':
          return '101vh'
        case 'large':
          return '90vh'
        case 'x-large':
          return '90vh'
        default:
          return '90vh'
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    setPopState () {
      history.pushState(null, document.title, window.location.href)
      window.addEventListener('popstate', this.handlePopState)
    },
    fetchInvoice() {
      const endpoint = this.isHq ? `/hq/invoices/${this.leadPid}` : `/invoices/${this.leadPid}`
      return this.$axios.get(endpoint).then(({ data }) => {
        this.invoice = data
        if (this.isHq) {
            this.$store.commit('hq/setSelectedCompany', data.company)
          }
        this.fetchActiveMedication()
      })
    },
    fetchTransactions () {
      const params = { company: this.isHq ? this.hqSelectedCompany.id : this.company.id, pid: this.leadPid }
      const endpoint = this.isHq ? '/hq/transactions' : '/transactions'
      return this.$axios.get(endpoint, { params }).then(({ data }) => { this.transactions = data.results || [] })
    },
    fetchTaxRates () {
      return this.$axios.get('/tax_rates?', { params: { company: this.isHq ? this.hqSelectedCompany.id : this.company.id } })
        .then(({ data }) => {
          if (data && data.results) {
            this.taxRates = data.results
          }
        })
    },
    onAddItemFromMedicalRecord() {
      this.openMedicationRecordDialog = true
    },
    async onTreatmentTableChanged() {
      await this.fetchInvoice()
    },
    async onMedicalRecordUpdated() {
      await this.fetchInvoice()
      this.treatmentTableKey++
    },
    onClickButton() {
      if (this.incompletedDocuments.length > 0) {
        this.showIncompleteDocumentConfirmationDialog = true
        return
      }
      let name = this.isHq ? 'HQCheckout' : 'ClinicBillingsList'
      if (this.origin) name = this.origin
      let route = { name }
      if (this.data && Object.keys(this.data).length) route = { name, ...this.data }
      this.$router.push(route)
    },
    onIgnoreIncompleteDocument () {
      this.incompletedDocuments = []
      this.onClickButton()
    },
    async onDownloadInvoice () {
      try {
        this.isLoadingDownloadInvoice = true
        const { data } = await this.$axios.get('/document_templates', { params: this.fetchInvoiceParams })
        if (data.count > 1) {
          this.invoiceTemplates = data.results
          this.openSelectInvoiceDialog = true
          this.isLoadingDownloadInvoice = false
          return
        }
        if (data.count === 1) {
          this.onPrint(data.results[0])
          return
        }
        this.showMessage(['error', this.$t('error_message.create_item_first', { item: this.$t('invoice').toLowerCase() })])
        if (this.isHq) {
          return
        }
        this.$router.push('/admin/clinic/settings/document/templates')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDownloadInvoice = false
      }
    },
    async onPrint (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingDownloadInvoice = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/invoices/${this.leadPid}/preview_document?document_template=${item.id}&key=${key}`
      this.isLoadingDownloadInvoice = false
      window.open(url,'_blank')
    },
    async refetch () {
      try {
        await this.fetchInvoice()
      // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    onDeleteAdjustment () {
      this.openConfirmDeleteAdjustmentDialog = true
    },
    async fetchActiveMedication () {
      this.activeMedications.isFetching = true
      const params = {
        page_size: -1,
        patient: this.patient.id,
        expand: 'medication'
      }
      return this.$axios.get(`/clinics/${this.company.slug}/active_medications`, { params })
        .then(({ data }) => {
          if (data?.results?.length > 0) {
            this.activeMedications.data = data.results
          } else {
            this.activeMedications.data = []
          }
        })
        .catch(() => {
          this.activeMedications.isError = true
        })
        .finally(() => {
          this.activeMedications.isFetching = false
        })
    },
    handlePopState() {
      this.onClickButton()
      history.pushState(null, document.title, window.location.href)
    },
  }
}
</script>

<style lang="scss" scoped>
.text-pricing {
  font-family: $body-font-family;
  font-size: 40px !important;
  line-height: 52px !important;
  letter-spacing: -0.038em !important;
}
.main {
  position: absolute;
  top: 0;
  width: 100% !important;
  height: var(--view-height) !important;
  overflow: auto;
}
</style>